import React, { useCallback, useState } from 'react';
import { StepControls } from '~/common/step-controls';
import { useSelector } from 'react-redux';
import { selectNewSong } from '~/features/dashboard/store/selectors';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import { useForm } from 'react-hook-form';
import { Box, Button, Chip, Grid, Switch, Typography } from '@material-ui/core';
import { INewSongStepFourFormInputs } from '~/models/music/new-song/formInputs';
import { createNewSongStepFourThunk, deleteISRC } from '~/features/dashboard/store/actions.thunks';
import { TextInput } from '~/features/dashboard/components/add-deal/components/text-input';
import { NamedForm } from '~/features/dashboard/components/add-song/components/named-form';
import { DatePickerController } from '~/features/dashboard/components/date-picker-controller';
import css from './styles.module.scss';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import classNames from 'classnames';
import { SongProjectTrack } from '~/models/music/new-song/newSongResponse.dto';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';
import { orderBy } from 'lodash';
import { useProfile } from '~/utils/hooks/useProfile';
import { DateFormat } from '~/common/date-format';
import { UpcForm } from '~/features/dashboard/components/add-song/components/step-4/UpcFrom';

interface Step_2Props {
    prevStep: () => void;
}

export const Step_4: React.FC<Step_2Props> = ({ prevStep }) => {
    const [defaultArtistName] = useProfile('defaultArtistName');
    const [defaultLabel] = useProfile('defaultLabel');
    const [UPC, setUPC] = useState('');
    const [upcList, setUpcList] = useState<number[]>([]);
    const [activeISRC, setActiveISRC] = useState('string');
    const [projectTrack, setProjectTrack] = useState<SongProjectTrack[]>([]);
    const [toggle, setToggle] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const newSongState = useSelector(selectNewSong);
    const dispatch = useThunkDispatch();

    const { handleSubmit, register, errors, control, setValue, getValues, setError, clearErrors } = useForm<INewSongStepFourFormInputs>({
        defaultValues: {
            firstDeal: newSongState?.gSongFirstDeal || '',
            isSongReleased: Boolean(newSongState?.isSongReleased) || false,
            songProjectTrack: newSongState?.songProjectTrack || [],
        },
    });

    const fillWithDefaults = () => {
        setValue('artist', String(defaultArtistName) || '', { shouldDirty: true });
        setValue('label', defaultLabel ? String(defaultLabel) : '', { shouldDirty: true });
    };

    const handleAddUPC = () => {
        UPC && setUpcList([...upcList, Number(UPC)]);
        setUPC('');
        setActiveISRC(getValues('isrc'));
    };

    const handleAddSingleUPC = (track: SongProjectTrack, upc: number) => {
        setProjectTrack([
            ...projectTrack.filter(({ isrc }) => isrc !== track.isrc),
            {
                ...track,
                upcList: [...track.upcList, upc],
            },
        ]);
    };

    const handleDeleteUPC = (upc: number) => () => {
        setUpcList(items => items.filter(item => item !== upc));
    };

    const handleDeleteSingleUPC = (track: SongProjectTrack, upc: number) => () => {
        setProjectTrack([
            ...projectTrack.filter(({ isrc }) => isrc !== track.isrc),
            {
                ...track,
                upcList: track.upcList.filter(item => item !== upc),
            },
        ]);
    };

    const deleteItem = async (isrc: string) => {
        await dispatch(deleteISRC({ isrc, keyDate: newSongState?.gProjectId, songref: newSongState?.gSongRef }));
        setProjectTrack([...projectTrack.filter(item => item.isrc !== isrc)]);
    };

    const editItem = (num: string) => {
        let element = projectTrack.filter(({ isrc }) => isrc === num);
        setValue('releaseDate', element[0].releaseDate);
        setValue('artist', element[0].trackArtist || '');
        setValue('track', element[0].trackTitle);
        setValue('isrc', element[0].isrc);
        setValue('label', element[0].trackLabel || '');
        setValue('firstDeal', newSongState?.gSongFirstDeal);
        setValue('isKeyTrack', element[0].isKeyTrack ? 1 : 0);
        setUpcList(element[0].upcList);
        setActiveISRC(element[0].isrc);
    };

    const onSave = () => {
        if (getValues('isrc')) {
            clearErrors('isrc');
            setProjectTrack([
                ...projectTrack.filter(({ isrc }) => isrc !== activeISRC),
                {
                    isrc: getValues('isrc'),
                    trackArtist: getValues('artist'),
                    trackLabel: getValues('label'),
                    trackTitle: getValues('track'),
                    releaseDate: getValues('releaseDate'),
                    upcList: UPC ? [...upcList, Number(UPC)] : upcList,
                    isKeyTrack: !!getValues('isKeyTrack'),
                },
            ]);
            setValue('releaseDate', '');
            setValue('firstDeal', '');
            setValue('artist', '');
            setValue('track', '');
            setValue('isrc', '');
            setValue('label', '');
            setValue('isKeyTrack', 0);
            setUpcList([]);
            setUPC('');
            setActiveISRC('');
        } else {
            setError('isrc', { message: 'Please input ISRC' });
        }
    };

    const onSubmit = useCallback(
        (data: INewSongStepFourFormInputs) => {
            setIsLoading(true);
            dispatch(createNewSongStepFourThunk({ ...data })).finally(() => setIsLoading(false));
        },
        [dispatch]
    );

    React.useEffect(() => {
        newSongState && setToggle(Boolean(newSongState?.isSongReleased));
        newSongState && setProjectTrack(newSongState?.songProjectTrack);
    }, [newSongState]);

    return (
        <NamedForm name={newSongState?.gSongName} onSubmit={handleSubmit(onSubmit)} action={{ label: 'Set Defaults', handler: fillWithDefaults }}>
            <Grid container spacing={4}>
                <Box hidden>
                    <Grid className="inputcustom-outer" item xs={9}>
                        <Typography className="publish-title" variant="h6">
                            Was the song released ?
                        </Typography>
                    </Grid>
                    <Grid className={classNames(css.gridRight, 'inputcustom-outer')} item xs={3}>
                        <span>No</span>
                        <Switch name="isSongReleased" checked={toggle} type="checkbox" inputRef={register} onChange={() => setToggle(!toggle)} />
                        <span>Yes</span>
                    </Grid>
                </Box>
                <Grid className="inputcustom-outer" item xs={4}>
                    <DatePickerController name="releaseDate" control={control} label="Release Date" row />
                </Grid>
                <Grid className="inputcustom-outer" item xs={4} hidden>
                    <TextInput className={css.textInput} label={'Deal Delivered To'} name={'firstDeal'} register={register} fieldError={errors.firstDeal} />
                </Grid>
                <Grid className="inputcustom-outer" item xs={8}>
                    <TextInput className={css.textInput} label={'Label'} name={'label'} register={register} />
                </Grid>
                <Grid className="inputcustom-outer" item xs={4} style={{ marginTop: '-2rem' }}>
                    <TextInput className={css.textInput} label={'Artist'} name={'artist'} register={register} />
                </Grid>
                <Grid className="inputcustom-outer" item xs={4} style={{ marginTop: '-2rem' }}>
                    <TextInput className={css.textInput} label={'Track'} name={'track'} register={register} />
                </Grid>
                <Grid className="inputcustom-outer" item xs={4} style={{ marginTop: '-2rem' }}>
                    <Grid container>
                        <Grid className="inputcustom-outer" item xs={12}>
                            <TextInput
                                className={css.textInput}
                                label={'Track (ISRC)'}
                                name={'isrc'}
                                register={register}
                                inputProps={{ maxLength: 12 }}
                                fieldError={errors.isrc}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid className="inputcustom-outer" item xs={6} style={{ marginTop: '-2rem' }} hidden>
                    <Grid container>
                        <Grid className="inputcustom-outer" style={{ position: 'relative', marginTop: '1rem' }} item xs={12}>
                            <Typography className={css.fieldName}>UPC (if known)</Typography>
                            <TextField
                                className={classNames(css.textInput, css.btnInput)}
                                variant={`filled`}
                                type={'number'}
                                value={UPC}
                                onChange={(e: any) => {
                                    e.target.value.length < 13 && setUPC(e.target.value);
                                }}
                                inputProps={{ maxLength: 13 }}
                            />
                            <Button onClick={handleAddUPC} className={css.addFeature} />
                        </Grid>
                        {upcList.map((item, num) => (
                            <Chip key={num} className={css.chip} label={item} onDelete={handleDeleteUPC(item)} />
                        ))}
                    </Grid>
                </Grid>
                <Grid className={css.gridRight} style={{ marginTop: '-1rem', marginBottom: '2rem' }} item xs={12}>
                    <Button className={css.saveButton} onClick={onSave} type={'button'}>
                        Save
                    </Button>
                </Grid>
            </Grid>
            {projectTrack && projectTrack.length > 0 && (
                <TableContainer component={Paper} className={css.tableKeyDates}>
                    <Table size="small" aria-label="a dense table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Release Date</TableCell>
                                <TableCell>Label</TableCell>
                                <TableCell>Artist</TableCell>
                                <TableCell style={{ minWidth: '200px' }}>Track</TableCell>
                                <TableCell>ISRC</TableCell>
                                <TableCell style={{ width: '10%' }}>UPC</TableCell>
                                <TableCell style={{ minWidth: '145px' }}>Key Tracks</TableCell>
                                <TableCell style={{ minWidth: '110px' }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orderBy(projectTrack, ['releaseDate'], ['asc']).map((item, num) => (
                                <TableRow key={num}>
                                    <TableCell>
                                        {item.releaseDate ? <DateFormat newDate={item.releaseDate} /> : 'MISSING'}
                                        <input type="hidden" name={`songProjectTrack[${num}].releaseDate`} value={String(item.releaseDate)} ref={register} />
                                    </TableCell>
                                    <TableCell>
                                        {item.trackLabel}
                                        <input type="hidden" name={`songProjectTrack[${num}].trackLabel`} value={String(item.trackLabel)} ref={register} />
                                    </TableCell>
                                    <TableCell>
                                        {item.trackArtist}
                                        <input type="hidden" name={`songProjectTrack[${num}].trackArtist`} value={String(item.trackArtist)} ref={register} />
                                    </TableCell>
                                    <TableCell>
                                        {item.trackTitle}
                                        <input type="hidden" name={`songProjectTrack[${num}].trackTitle`} value={String(item.trackTitle)} ref={register} />
                                    </TableCell>
                                    <TableCell>
                                        {item.isrc}
                                        <input type="hidden" name={`songProjectTrack[${num}].isrc`} value={String(item.isrc)} ref={register} />
                                    </TableCell>
                                    <TableCell>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12}>
                                                <UpcForm id={item.isrc} onSubmit={(newUpc: number) => handleAddSingleUPC(item, newUpc)} />
                                            </Grid>
                                            {!!(item.upcList.length) && (
                                                <Grid item xs={12}>
                                                    {item.upcList.map(
                                                        (upc, index) =>
                                                            upc && (
                                                                <Box key={upc}>
                                                                    <input
                                                                        type="hidden"
                                                                        name={`songProjectTrack[${num}].upcList[${index}]`}
                                                                        value={upc}
                                                                        ref={register}
                                                                    />
                                                                    <Chip
                                                                        key={index}
                                                                        className={css.chip}
                                                                        label={upc}
                                                                        onDelete={handleDeleteSingleUPC(item, upc)}
                                                                    />
                                                                </Box>
                                                            )
                                                    )}
                                                </Grid>
                                            )}
                                        </Grid>
                                    </TableCell>
                                    <TableCell className="inputcustom-outer">
                                        <span>No</span>
                                        <Switch
                                            name={`songProjectTrack[${num}].isKeyTrack`}
                                            type="checkbox"
                                            inputRef={register}
                                            defaultChecked={!!item.isKeyTrack}
                                        />
                                        <span>Yes</span>
                                    </TableCell>
                                    <TableCell className={css.cellAction}>
                                        <DeleteOutlineIcon titleAccess="Delete" className={css.deleteIcon} onClick={() => deleteItem(item.isrc)} />
                                        <EditIcon titleAccess="Edit" className={css.editIcon} onClick={() => editItem(item.isrc)} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <StepControls onPrevClick={prevStep} className={css.nextButton} isSubmitting={isLoading} />
        </NamedForm>
    );
};
